import type { ReactNode, MutableRefObject } from 'react';
import {
	useState,
	Fragment,
	useMemo,
	createContext,
	useContext,
	useRef,
} from 'react';
import DOM from 'react-dom';
import styled, { ThemeProvider, useTheme } from 'styled-components';

import * as breakpoints from 'pkg/config/breakpoints';

import { cssClasses, type CSSPropertiesWithVar } from 'pkg/css/utils';
import useComponentDidMount from 'pkg/hooks/useComponentDidMount';
import { useNewTopIndex } from 'pkg/hooks/useTopIndex';
import uuid from 'pkg/uuid';

import { Backdrop } from 'components/backdrop';
import MaterialSymbol from 'components/material-symbols';

import * as cardCss from 'design/card/styles.css';

import * as css from './styles.css';

/**
 * Keep as styled component to handle the card styling overrides
 */
const ModalBase = styled.div`
	.${cardCss.heading} {
		color: var(--palette-gray-800);
		font-size: var(--font-size-lg);
		font-weight: var(--font-weight-semibold);
		text-align: left;
		overflow: hidden;
		white-space: nowrap;
		text-overflow: ellipsis;
		vertical-align: top;
	}

	> .${cardCss.base} {
		padding: 0;
		margin: 0;
		display: flex;
		flex-flow: column;
		overflow: hidden;
		max-height: 100%;
	}

	.${cardCss.body} {
		overflow: auto;
		-webkit-overflow-scrolling: touch;
	}

	&.${css.hasMinHeight} > .${cardCss.base} {
		flex: 1 1 auto;
	}

	&.${css.hasMinHeight} > .${cardCss.base} > .${cardCss.body} {
		flex-grow: 1;
	}

	@media ${breakpoints.small} {
		.${cardCss.heading}:last-child:first-child {
			text-align: center;
			margin: 0;
			width: 100%;
		}

		.${cardCss.footer} {
			padding: var(--spacing-5);
			border-radius: 0;
		}

		> .${cardCss.base} {
			height: 100%;
			width: 100%;
			max-height: 100%;
			border-bottom-left-radius: 0;
			border-bottom-right-radius: 0;
		}
	}
`;

interface ModalContext {
	inModal: boolean;
	activeModalId: string;
	addModal?: (id: string) => void;
	removeModal?: (id: string) => void;
}

export const modalsContext = createContext<ModalContext>({
	inModal: false,
	activeModalId: '',
});

export function useModalContext() {
	return useContext(modalsContext);
}

interface ModalContextProps {
	modalId: string;
	children: ReactNode;
}

function ModalContextProvider({ modalId, children }: ModalContextProps) {
	const modals = useRef<Set<string>>(new Set<string>().add(modalId));
	const [activeId, setActiveId] = useState(modalId);
	const addModal = (id: string) => {
		modals.current = modals.current.add(id);
		setActiveId(id);
	};
	const removeModal = (id: string) => {
		modals.current.delete(id);
		setActiveId([...modals.current].pop());
	};

	const values = useMemo(
		() => ({
			activeModalId: activeId,
			inModal: activeId !== '',
			addModal,
			removeModal,
		}),
		[activeId]
	);

	return (
		<modalsContext.Provider value={values}>{children}</modalsContext.Provider>
	);
}

const ModalContextConsumer = ({ modalId, children }: ModalContextProps) => {
	const ctx = useContext(modalsContext);

	useComponentDidMount(
		() => {
			ctx.addModal(modalId);
		},
		() => {
			ctx.removeModal(modalId);
		}
	);

	return children;
};

const openLayers: string[] = [];

export function useModalLayer(): () => boolean {
	const [layerId] = useState(() => uuid());

	useComponentDidMount(
		() => {
			openLayers.push(layerId);
		},
		() => {
			openLayers.splice(openLayers.indexOf(layerId), 1);
		}
	);

	return () => openLayers[openLayers.length - 1] === layerId;
}

interface InnerProps {
	thin: boolean;
	wide: boolean;
	centered: boolean;
	fullScreen: boolean;
	minHeight?: number | string;
	hideClose: boolean;
	handleAnimationEnd: () => void;
	animationDirection: 'in' | 'out';
	close: () => void;
	children: ReactNode;
	modalId: string;
	currentIndex: number;
}

function Inner({
	thin,
	wide,
	centered,
	fullScreen,
	minHeight,
	hideClose,
	handleAnimationEnd,
	animationDirection,
	close,
	children,
	modalId,
	currentIndex,
}: InnerProps) {
	const ctx = useContext(modalsContext);
	const theme = useTheme();

	const style: CSSPropertiesWithVar = { zIndex: currentIndex + 1 };

	if (minHeight !== undefined) {
		style['--modal-min-height'] =
			typeof minHeight === 'number' ? `${minHeight}px` : minHeight;
	}

	return (
		<ThemeProvider
			theme={{
				...theme,
				animationDirection,
			}}>
			<div
				className={cssClasses(
					css.modalWrapper,
					ctx.activeModalId !== modalId && css.isPassive
				)}
				role="dialog"
				id="modal-wrapper">
				<ModalBase
					className={cssClasses(
						css.modalBase,
						wide && css.wide,
						thin && css.thin,
						centered && css.centered,
						fullScreen && css.fullScreen,
						minHeight !== undefined && css.hasMinHeight,
						animationDirection === 'in' ? css.animatingIn : css.animatingOut
					)}
					style={style}
					onAnimationEnd={handleAnimationEnd}>
					{!hideClose && (
						<span
							className={css.closeButton}
							onClick={close}
							data-testid="modal.close">
							<MaterialSymbol variant="close" />
						</span>
					)}
					{children}
				</ModalBase>
			</div>
		</ThemeProvider>
	);
}

type EscapeHandler = (event: KeyboardEvent) => void;

interface ModalProps {
	onClose?: () => void;
	customBackdrop?: ReactNode;
	children?: ReactNode;
	thin?: boolean;
	wide?: boolean;
	fullScreen?: boolean;
	minHeight?: number | string;
	hideClose?: boolean;
	centered?: boolean;
	forwardedRef?: MutableRefObject<unknown>;
	// Whether Escape and the X may close the modal, defaults to `!hideClose`.
	canClose?: boolean;
	// Asked before the X or Escape closes the modal, returning false keeps it open.
	onBeforeClose?: () => boolean;
}

function Modal({
	onClose,
	customBackdrop,
	children,
	thin,
	wide,
	fullScreen,
	minHeight,
	hideClose,
	centered,
	forwardedRef,
	canClose = !hideClose,
	onBeforeClose,
}: ModalProps) {
	const [animationDirection, setAnimationDirection] = useState<'in' | 'out'>(
		'in'
	);
	const ctx = useContext(modalsContext);
	const isFirstModal = ctx.activeModalId === '';
	const [modalId] = useState(() => {
		const id = uuid();
		return id;
	});

	const currentIndex = useNewTopIndex();

	const isTopLayer = useModalLayer();

	const close = () => {
		setAnimationDirection('out');
	};

	const requestClose = () => {
		if (animationDirection === 'out') {
			return;
		}

		if (!canClose || onBeforeClose?.() === false) {
			return;
		}

		close();
	};

	if (forwardedRef) {
		forwardedRef.current = { close, requestClose };
	}

	const handleEscape = useRef<EscapeHandler>(null);

	// A keydown already claimed with preventDefault, such as an autocomplete's open list, is left alone.
	handleEscape.current = (event) => {
		if (event.key !== 'Escape' || event.defaultPrevented || !isTopLayer()) {
			return;
		}

		// The browser closes an open native dialog on this Escape, so the modal under it stays.
		if (
			event.target instanceof Element &&
			event.target.closest('dialog[open]')
		) {
			return;
		}

		requestClose();
	};

	const listener = (event: KeyboardEvent) => handleEscape.current(event);

	useComponentDidMount(
		() => {
			document.addEventListener('keydown', listener);
		},
		() => {
			document.removeEventListener('keydown', listener);
		}
	);

	const handleAnimationEnd = () => {
		if (animationDirection === 'out') {
			onClose?.();
		}
	};

	useComponentDidMount(
		() => {
			document.body.classList.add('modal-open');
		},
		() => {
			if (isFirstModal) {
				document.body.classList.remove('modal-open');
			}
		}
	);

	const backdrop = customBackdrop || (
		<Backdrop animationDirection={animationDirection} zIndex={currentIndex} />
	);

	const content = (
		<Inner
			handleAnimationEnd={handleAnimationEnd}
			thin={thin}
			wide={wide}
			centered={centered}
			fullScreen={fullScreen}
			minHeight={minHeight}
			hideClose={hideClose}
			animationDirection={animationDirection}
			close={requestClose}
			modalId={modalId}
			currentIndex={currentIndex}>
			{children}
		</Inner>
	);

	return DOM.createPortal(
		<Fragment>
			{isFirstModal && backdrop}
			{isFirstModal ? (
				<ModalContextProvider modalId={modalId}>
					<ModalContextConsumer modalId={modalId}>
						{content}
					</ModalContextConsumer>
				</ModalContextProvider>
			) : (
				<ModalContextConsumer modalId={modalId}>{content}</ModalContextConsumer>
			)}
		</Fragment>,
		document.getElementById('modal-portal-container')
	);
}

export default Modal;
